import * as React from 'react';
import propz from 'propz';
import { AppUser } from '../../../../../views/App/App';
import { CLUB_PROGRAMME_TABS_ARRAY, CLUB_PROGRAMME_TABS } from '../../../../../consts/clubProgramme';
import { AfterSchoolClubProgramme } from '../../../../../models/clubProgramme';
import { getClubProgrammeStatus, getClubProgrammeClubsCount } from '../../../../../helpers/accessor/accessor';
import { ClubProgrammeSummaryTab } from './ClubProgrammeSummaryTab';
import { ClubProgrammeClubsTab } from './ClubProgrammeClubsTab';
import { ClubProgrammeBookingsTab } from './ClubProgrammeBookingsTab';
import { ClubProgrammeFieldsEditor } from '../ClubProgrammeFieldsEditor';

const ICON_PATHS = {
  layers: 'M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  ticket: 'M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4zM9 6v12'
};

const DsIcon: React.FC<{ icon: keyof typeof ICON_PATHS; size?: number }> = ({ icon, size = 16 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.85"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={ICON_PATHS[icon]} />
  </svg>
);

const toTitleCase = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .replace(
      /(^|\s|_)([a-z])/g,
      (_, separator, letter) => `${separator === '_' ? ' ' : separator}${letter.toUpperCase()}`
    );

const getStatusTone = (status: string): string => String(status || 'draft').toLowerCase();

const getSelectionModeLabel = (programme: AfterSchoolClubProgramme): string => {
  const mode = String(propz.get(programme, ['selectionRules', 'mode'], 'SINGLE') || 'SINGLE').toUpperCase();
  if (mode !== 'PRIORITY') return 'Single';

  const min = Number(propz.get(programme, ['selectionRules', 'priorityRange', 'min'], 1));
  const max = Number(propz.get(programme, ['selectionRules', 'priorityRange', 'max'], 10));
  const safeMin = Number.isFinite(min) && min > 0 ? Math.trunc(min) : 1;
  const safeMax = Number.isFinite(max) && max >= safeMin ? Math.trunc(max) : safeMin;

  return `Priority ${safeMin}-${safeMax}`;
};

type Props = {
  user: AppUser;
  selectedProgramme?: AfterSchoolClubProgramme;
  clubDetailsById?: { [clubId: string]: any };
  currentTabIndex: number;
  onTabClick: (event: any, index: number) => void;
  onProgrammeUpdated: (updated: AfterSchoolClubProgramme) => void;
  onOpenManageClubsPage: (programme: AfterSchoolClubProgramme) => void;
  onOpenBookingsManager: (programme: AfterSchoolClubProgramme) => void;
  onClose?: () => void;
};

export const ClubProgrammesSummaryPanel: React.FC<Props> = ({
  user,
  selectedProgramme,
  clubDetailsById = {},
  currentTabIndex,
  onTabClick,
  onProgrammeUpdated,
  onOpenManageClubsPage,
  onOpenBookingsManager,
  onClose
}) => {
  if (!selectedProgramme) {
    return null;
  }

  const tabs = CLUB_PROGRAMME_TABS_ARRAY;
  const currentTab = tabs[currentTabIndex];

  const programmeStatus = getClubProgrammeStatus(selectedProgramme);
  const clubsCount = getClubProgrammeClubsCount(selectedProgramme);
  const canEditClubs = String(programmeStatus || '') === 'DRAFT';
  const programmeName = String((selectedProgramme as any)?.name || 'Programme');
  const programmeStatusLabel = toTitleCase(programmeStatus || 'DRAFT');
  const selectionModeLabel = getSelectionModeLabel(selectedProgramme);

  const defaultFields = Array.isArray((selectedProgramme as any)?.defaultFields)
    ? (selectedProgramme as any).defaultFields
    : [];
  const formQuestionCount = Array.isArray((selectedProgramme as any)?.formDefinition?.elements)
    ? (selectedProgramme as any).formDefinition.elements.filter((element: any) => element?.kind === 'QUESTION').length
    : 0;

  const hasNoClubs = clubsCount === 0;
  const hasNoDefaultFields = (formQuestionCount || defaultFields.length) === 0;

  const getWarningText = (tab: string): string => {
    if (tab === CLUB_PROGRAMME_TABS.CLUBS && hasNoClubs) return 'No clubs added yet';
    if (tab === CLUB_PROGRAMME_TABS.FIELDS && hasNoDefaultFields) return 'No default fields configured';
    return '';
  };

  return (
    <div className="cpDrawer" role="dialog" aria-modal="true" aria-label="Programme details">
      <div className="cpDrawerHead">
        <div className="cpDrawerLead">
          <DsIcon icon="layers" size={22} />
        </div>
        <div className="cpDrawerTitles">
          <div className="cpDrawerTitle">{programmeName}</div>
          <div className="cpDrawerMeta">
            <span className={`cpDrawerStatus cpDrawerStatus--${getStatusTone(programmeStatus)}`}>
              {programmeStatusLabel}
            </span>
            <i />
            <span>{selectionModeLabel}</span>
            <i />
            <span>{clubsCount} clubs</span>
          </div>
        </div>
        {onClose ? (
          <button type="button" className="cpDrawerClose" aria-label="Close programme details" onClick={onClose}>
            <svg viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        ) : null}
      </div>

      <div className="cpDrawerTabs">
        {tabs.map((tab, index) => {
          const warningText = getWarningText(tab);
          const isActive = index === currentTabIndex;

          return (
            <button
              key={tab}
              type="button"
              className={isActive ? 'sit-tab sit-tab--active isActive' : 'sit-tab'}
              onClick={event => onTabClick(event, index)}
              title={warningText || String(tab)}
            >
              <span>{tab}</span>
              {warningText ? <span className="cpDrawerTabDot" aria-hidden="true" /> : null}
            </button>
          );
        })}
      </div>

      <div className="cpDrawerBody">
        {currentTab === CLUB_PROGRAMME_TABS.SUMMARY && (
          <ClubProgrammeSummaryTab user={user} programme={selectedProgramme} />
        )}

        {currentTab === CLUB_PROGRAMME_TABS.CLUBS && (
          <ClubProgrammeClubsTab
            user={user}
            programme={selectedProgramme}
            clubDetailsById={clubDetailsById}
            status={programmeStatus}
            canEdit={canEditClubs}
          />
        )}

        {currentTab === CLUB_PROGRAMME_TABS.FIELDS && (
          <ClubProgrammeFieldsEditor
            user={user}
            programme={selectedProgramme}
            onProgrammeUpdated={onProgrammeUpdated}
          />
        )}

        {currentTab === CLUB_PROGRAMME_TABS.BOOKINGS && (
          <ClubProgrammeBookingsTab
            programme={selectedProgramme}
            status={programmeStatus}
            onOpenBookingsManager={() => onOpenBookingsManager(selectedProgramme)}
          />
        )}
      </div>

      <div className="cpDrawerFoot">
        <button
          type="button"
          className="cpDsBtn cpDsBtnSecondary cpDrawerActionBtn"
          onClick={() => onOpenBookingsManager(selectedProgramme)}
        >
          <DsIcon icon="ticket" size={16} />
          <span>Bookings</span>
        </button>
        <button
          type="button"
          className="cpDsBtn cpDsBtnPrimary cpDrawerActionBtn"
          onClick={() => onOpenManageClubsPage(selectedProgramme)}
        >
          <DsIcon icon="layers" size={16} />
          <span>Manage clubs</span>
        </button>
      </div>
    </div>
  );
};

ClubProgrammesSummaryPanel.displayName = 'ClubProgrammesSummaryPanel';
